/**
 * Shared React Query retry policy for gateway reads.
 *
 * Retry only transport/transient failures, never a definitive HTTP 4xx.
 * Every query owner for the same endpoint imports this one predicate so the
 * retry behavior cannot drift between hooks.
 */
import { classifyError, getResponseStatus } from './client';

export function shouldRetryTransientError(
  failureCount: number,
  error: unknown,
  maxRetries = 2,
): boolean {
  return classifyError(error).kind === 'transient' && failureCount < maxRetries;
}

// 408 and 429 say "not now", so asking again later can succeed.
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

/** True when the server answered with a 4xx that asking again would only repeat. */
export function isDeterministicClientError(error: unknown): boolean {
  const status = getResponseStatus(error);
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_CLIENT_STATUSES.has(status)
  );
}

/**
 * Application-wide query default. A deterministic 4xx is the server's answer,
 * not a hiccup: re-sending it repeats the same refusal and multiplies the
 * request, so only other failures get React Query's usual three retries.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return !isDeterministicClientError(error) && failureCount < 3;
}
